import React from 'react';
import {
  Music, Swords, Wand2, TreePine, Ghost, Flame, Footprints,
  Play, Pause, SkipForward, SkipBack, Volume2, Repeat, Shuffle,
  Star, Square, AudioLines
} from 'lucide-react';

export function AudioPlayerMockup() {
  const soundboardPads = [
    { name: 'Cri de Guerre', icon: Swords, type: 'oneshot', active: false, color: 'text-red-400' },
    { name: 'Sort Arcanique', icon: Wand2, type: 'oneshot', active: false, color: 'text-purple-400' },
    { name: 'Pluie Battante', icon: TreePine, type: 'loop', active: true, color: 'text-green-400' },
    { name: 'Fantôme', icon: Ghost, type: 'oneshot', active: false, color: 'text-slate-400' },
    { name: 'Explosion', icon: Flame, type: 'oneshot', active: false, color: 'text-orange-400' },
    { name: 'Pas Furtifs', icon: Footprints, type: 'loop', active: true, color: 'text-cyan-400' },
  ];

  const categories = [
    { name: 'Combat', count: 18, active: false },
    { name: 'Donjon', count: 12, active: true },
    { name: 'Taverne', count: 8, active: false },
    { name: 'Forêt', count: 15, active: false },
  ];

  return (
    <div className="landing-mockup-card">
      <div className="bg-gradient-to-r from-pink-500/10 via-purple-500/10 to-blue-500/10 rounded-lg p-2.5 mb-3 border border-white/5">
        <div className="flex items-center gap-2.5 mb-1.5">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-pink-600 to-purple-700 flex items-center justify-center flex-shrink-0 landing-audio-vinyl">
            <Music className="h-3.5 w-3.5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-bold truncate">Tavern Ambiance</div>
            <div className="text-[9px] text-muted-foreground">Medieval Moods</div>
          </div>
          <Star className="h-3 w-3 text-amber-400 fill-amber-400 flex-shrink-0" />
        </div>
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[8px] font-mono text-muted-foreground w-6">2:56</span>
          <div className="flex-1 h-1 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-pink-500 to-purple-500 landing-audio-progress" style={{ width: '65%' }} />
          </div>
          <span className="text-[8px] font-mono text-muted-foreground w-6">4:32</span>
        </div>
        <div className="flex items-center justify-center gap-2.5">
          <Shuffle className="h-2.5 w-2.5 text-muted-foreground" />
          <SkipBack className="h-3 w-3 text-muted-foreground" />
          <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
            <Pause className="h-3 w-3 text-primary-foreground" />
          </div>
          <SkipForward className="h-3 w-3 text-muted-foreground" />
          <Repeat className="h-2.5 w-2.5 text-primary" />
          <div className="ml-1 flex items-center gap-1">
            <Volume2 className="h-2.5 w-2.5 text-muted-foreground" />
            <div className="w-10 h-0.5 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-muted-foreground/50 rounded-full" style={{ width: '70%' }} />
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-border/30 pt-2.5">
        <div className="flex items-center gap-2 mb-2">
          <AudioLines className="h-3.5 w-3.5 text-amber-400" />
          <span className="text-[10px] font-bold">Soundboard</span>
          <span className="text-[8px] bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded-full ml-auto flex items-center gap-0.5">
            <span className="w-1 h-1 rounded-full bg-green-400 animate-pulse" /> 2 actifs
          </span>
        </div>

        <div className="flex gap-1 mb-2 overflow-x-auto">
          {categories.map((cat) => (
            <span key={cat.name} className={`text-[8px] px-2 py-0.5 rounded-full whitespace-nowrap cursor-pointer transition-colors ${cat.active ? 'bg-primary/20 text-primary border border-primary/30' : 'bg-muted/50 text-muted-foreground border border-transparent hover:bg-muted'}`}>
              {cat.name} ({cat.count})
            </span>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-1.5">
          {soundboardPads.map((pad, i) => {
            const PadIcon = pad.icon;
            return (
              <div
                key={i}
                className={`relative rounded-lg border p-1.5 text-center cursor-pointer transition-all ${pad.active
                  ? 'bg-primary/10 border-primary/40 ring-1 ring-primary/20'
                  : 'bg-muted/30 border-border/30 hover:bg-muted/50 hover:border-border/50'
                }`}
              >
                {pad.active && (
                  <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                )}
                <PadIcon className={`h-3.5 w-3.5 mx-auto mb-0.5 ${pad.active ? 'text-primary' : pad.color}`} />
                <div className="text-[7px] truncate text-muted-foreground leading-tight">{pad.name}</div>
                <div className={`text-[6px] mt-0.5 ${pad.type === 'loop' ? 'text-purple-400' : 'text-amber-400'}`}>
                  {pad.type === 'loop' ? 'Loop' : 'One-shot'}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-border/20">
          <Volume2 className="h-3 w-3 text-muted-foreground" />
          <span className="text-[8px] text-muted-foreground">Master</span>
          <div className="flex-1 h-1 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-muted-foreground/40 rounded-full" style={{ width: '80%' }} />
          </div>
          <span className="text-[8px] text-muted-foreground font-mono">80%</span>
          <button className="text-[7px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 border border-red-500/20 flex items-center gap-0.5">
            <Square className="h-2 w-2" /> Stop all
          </button>
        </div>
      </div>
    </div>
  );
}
